import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

/*
 * Highlights search hits (yellow) and powers Find & Replace in a note.
 * Matching is case-insensitive substring, same as the search compiler.
 */

export interface Match {
  from: number;
  to: number;
}

interface State {
  terms: string[];
  active: number; // index into matches for Find; -1 = none
  matches: Match[];
  deco: DecorationSet;
}

export const searchKey = new PluginKey<State>("engramSearch");

export function findMatches(doc: PMNode, terms: string[]): Match[] {
  const needles = terms.map((t) => t.toLowerCase()).filter(Boolean);
  if (!needles.length) return [];
  const out: Match[] = [];
  // Walk textblocks so a match can span marks inside one paragraph.
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    let text = "";
    node.forEach((child) => {
      text += child.isText ? child.text! : "￼";
    });
    const lower = text.toLowerCase();
    // Most paragraphs have no hit: the position map (one entry per character) is built only for those that do,
    // since this runs on every keystroke in a note opened from search results.
    if (!needles.some((n) => lower.includes(n))) return false;
    const map: number[] = [];
    node.forEach((child, offset) => {
      if (child.isText) for (let i = 0; i < child.text!.length; i++) map.push(pos + 1 + offset + i);
      else map.push(pos + 1 + offset);
    });
    for (const n of needles) {
      let i = lower.indexOf(n);
      while (i >= 0) {
        out.push({ from: map[i]!, to: map[i + n.length - 1]! + 1 });
        i = lower.indexOf(n, i + n.length);
      }
    }
    return false;
  });
  return out.sort((a, b) => a.from - b.from);
}

function build(doc: PMNode, terms: string[], active: number): State {
  const matches = findMatches(doc, terms);
  const deco = DecorationSet.create(
    doc,
    matches.map((m, i) => Decoration.inline(m.from, m.to, { class: i === active ? "search-hit active" : "search-hit" })),
  );
  return { terms, active, matches, deco };
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    searchHighlight: {
      setSearch: (terms: string[], active?: number) => ReturnType;
    };
  }
}

export const SearchHighlight = Extension.create({
  name: "searchHighlight",
  addCommands() {
    return {
      setSearch:
        (terms, active = -1) =>
        ({ tr, dispatch }) => {
          if (dispatch) tr.setMeta(searchKey, { terms, active });
          return true;
        },
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin<State>({
        key: searchKey,
        state: {
          init: (_, { doc }) => build(doc, [], -1),
          apply(tr, prev, _old, state) {
            const meta = tr.getMeta(searchKey) as { terms: string[]; active: number } | undefined;
            if (meta) return build(state.doc, meta.terms, meta.active);
            if (tr.docChanged) return build(state.doc, prev.terms, prev.active);
            return prev;
          },
        },
        props: {
          decorations: (state) => searchKey.getState(state)?.deco,
        },
      }),
    ];
  },
});
